import { openDB, IDBPDatabase } from 'idb';
import { StoredState } from './types';
import { migrateAndValidateState } from './validation';
import { DEFAULT_SETTINGS, DEFAULT_DOCUMENT, CURRENT_SCHEMA_VERSION } from './defaults';

const DB_NAME = 'tp_teleprompter_db';
const DB_VERSION = 1;
const STORE_NAME = 'app_state';
const STATE_KEY = 'current_state';
const BACKUP_KEY = 'backup_corrupt_state';

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      },
    });
  }
  return dbPromise;
}

/**
 * Loads persistent state with automatic validation and fallback
 */
export async function loadPersistentState(): Promise<StoredState> {
  try {
    const db = await getDB();
    const raw = await db.get(STORE_NAME, STATE_KEY);
    if (!raw) {
      return {
        schemaVersion: CURRENT_SCHEMA_VERSION,
        settings: { ...DEFAULT_SETTINGS },
        document: { ...DEFAULT_DOCUMENT },
      };
    }
    return migrateAndValidateState(raw);
  } catch (err) {
    console.warn('[TP Persistence] Failed to load from IndexedDB, trying localStorage fallback:', err);
    try {
      const rawLocal = localStorage.getItem(STATE_KEY);
      if (rawLocal) {
        return migrateAndValidateState(JSON.parse(rawLocal));
      }
    } catch {
      // ignore
    }
    return {
      schemaVersion: CURRENT_SCHEMA_VERSION,
      settings: { ...DEFAULT_SETTINGS },
      document: { ...DEFAULT_DOCUMENT },
    };
  }
}

/**
 * Saves state to IndexedDB and mirrors to localStorage fallback
 */
export async function savePersistentState(state: StoredState): Promise<void> {
  try {
    const db = await getDB();
    await db.put(STORE_NAME, state, STATE_KEY);
  } catch (err) {
    console.warn('[TP Persistence] IndexedDB write failed:', err);
  }

  // Backup mirror
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota errors
  }
}

/**
 * Copies unparsable or corrupt data to a backup key
 */
export async function backupCorruptData(corrupt: unknown): Promise<void> {
  try {
    const db = await getDB();
    await db.put(STORE_NAME, corrupt, BACKUP_KEY);
  } catch {
    // ignore
  }
}
